// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

/**
 * Secure logout utility
 *
 * Clears all session-sensitive data from browser storage.
 * Project switching requires full logout and re-login.
 *
 * Storage cleanup:
 * - Clears ALL sessionStorage (PHI drafts, temp state)
 * - Clears project-specific localStorage (organization ID)
 * - Preserves user preferences (language, theme, sidebar mode)
 */

// Keys that contain project/session-specific data and MUST be cleared on logout
const PROJECT_SPECIFIC_KEYS = [
  'carepoint-current-organization',
  // Old membership keys (may exist from previous versions)
  'carepoint_memberships',
  'carepoint_login_id',
];

// Medplum SDK storage keys that should be cleared
// These are the default keys used by MedplumClient for token storage
const MEDPLUM_STORAGE_KEYS = [
  'medplum-ts-token-storage',
  'medplum-activeLogin',
  'medplum-refreshToken',
];

/**
 * Clears all session and project-specific data from storage.
 * Call this BEFORE medplum.signOut() to ensure complete cleanup.
 */
export function clearSessionStorage(): void {
  console.log('[SecureLogout] Clearing session storage...');

  // Clear all sessionStorage (contains PHI drafts and temp state)
  try {
    sessionStorage.clear();
    console.log('[SecureLogout] SessionStorage cleared');
  } catch (e) {
    console.error('[SecureLogout] Error clearing sessionStorage:', e);
  }

  // Clear project-specific localStorage keys
  for (const key of PROJECT_SPECIFIC_KEYS) {
    try {
      localStorage.removeItem(key);
      console.log(`[SecureLogout] Removed localStorage key: ${key}`);
    } catch (e) {
      console.error(`[SecureLogout] Error removing ${key}:`, e);
    }
  }

  // Clear Medplum SDK storage keys (belt and suspenders with signOut)
  for (const key of MEDPLUM_STORAGE_KEYS) {
    try {
      localStorage.removeItem(key);
      console.log(`[SecureLogout] Removed Medplum key: ${key}`);
    } catch (e) {
      console.error(`[SecureLogout] Error removing ${key}:`, e);
    }
  }

  console.log('[SecureLogout] Session storage cleanup complete');
}

/**
 * Gets the list of storage keys that will be cleared on logout.
 * Useful for debugging/testing.
 */
export function getSessionStorageKeys(): { projectKeys: string[]; medplumKeys: string[] } {
  return {
    projectKeys: [...PROJECT_SPECIFIC_KEYS],
    medplumKeys: [...MEDPLUM_STORAGE_KEYS],
  };
}
